import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import SellOutlined from "@mui/icons-material/SellOutlined";
import type { Category } from "../../../redux/types/categoryTypes";
import type { LocalDraft } from "./drafts";
import { SellingValues, stepLabels } from "./types";
import { stepErrors } from "./validation";
type Confirmation = "discard" | "resume" | null;
export function ListingHeader() {
  return (
    <Box
      sx={{
        background: "linear-gradient(115deg, #233647, #374d5d)",
        color: "white",
        py: { xs: 3, sm: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{ p: 1.25, bgcolor: "rgba(231,135,88,.16)", borderRadius: 3 }}
          >
            <SellOutlined sx={{ color: "#ffc6a9", fontSize: 28 }} />
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: "#ffc6a9", letterSpacing: 1.7 }}
            >
              FiddlePiddle sellers
            </Typography>
            <Typography component="h1" variant="h4" fontWeight={750}>
              List Your Toy
            </Typography>
          </Box>
        </Stack>
        <Typography sx={{ mt: 1.5, color: "#e0e7ec", maxWidth: 640 }}>
          Make room for the next adventure. Give a toy or collectible a new
          home.
        </Typography>
      </Container>
    </Box>
  );
}
export function DraftControls({
  savedDraft,
  photoBusy,
  hasPhotos,
  isDirty,
  notice,
  draftError,
  missingPhotos,
  save,
  resume,
  setConfirmation,
  setNotice,
  setDraftError,
}: {
  savedDraft: LocalDraft | null;
  photoBusy: boolean;
  hasPhotos: boolean;
  isDirty: boolean;
  notice: string;
  draftError: string;
  missingPhotos: string[];
  save: () => void;
  resume: () => void;
  setConfirmation: (value: Confirmation) => void;
  setNotice: (value: string) => void;
  setDraftError: (value: string) => void;
}) {
  return (
    <>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          Your listing stays private until publishing is available.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            type="button"
            startIcon={<SaveOutlined />}
            onClick={save}
            disabled={photoBusy}
          >
            Save Draft
          </Button>
          <Button
            size="small"
            type="button"
            color="secondary"
            onClick={() => setConfirmation("discard")}
            disabled={photoBusy}
          >
            Discard Draft
          </Button>
        </Stack>
      </Stack>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {savedDraft && (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={photoBusy}
                onClick={() =>
                  isDirty || hasPhotos ? setConfirmation("resume") : resume()
                }
              >
                Resume Draft
              </Button>
            }
          >
            A draft is saved for this account on this device. Last saved{" "}
            {new Date(savedDraft.savedAt).toLocaleString()}.
          </Alert>
        )}
        {notice && (
          <Alert severity="success" onClose={() => setNotice("")}>
            {notice}
          </Alert>
        )}
        {draftError && (
          <Alert severity="error" onClose={() => setDraftError("")}>
            {draftError}
          </Alert>
        )}
        {!!missingPhotos.length && (
          <Alert severity="warning">
            Photo files weren't stored in your draft. Reselect these photos:{" "}
            {missingPhotos.join(", ")}.
          </Alert>
        )}
      </Stack>
    </>
  );
}
export function WizardProgress({
  step,
  furthest,
  values,
  categories,
  hasPhotos,
  photoBusy,
  goTo,
}: {
  step: number;
  furthest: number;
  values: SellingValues;
  categories: Category[];
  hasPhotos: boolean;
  photoBusy: boolean;
  goTo: (step: number) => void;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: 3, p: { xs: 2, sm: 2.5 }, mb: 3 }}
    >
      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <Stepper nonLinear activeStep={step}>
          {stepLabels.map((label, index) => (
            <Step
              key={label}
              completed={
                index < furthest &&
                (index === 4 ||
                  (!stepErrors(index, values, categories).length &&
                    (index !== 1 || hasPhotos)))
              }
            >
              <StepButton
                disabled={
                  index > furthest || photoBusy || (index === 4 && step !== 4)
                }
                onClick={() => goTo(index)}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          gap={1}
          sx={{ mb: 1.5 }}
        >
          <Typography fontWeight={700} color="secondary.main">
            {stepLabels[step]}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {step + 1} of 5
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={(step + 1) * 20}
          aria-label={`Step ${step + 1} of 5: ${stepLabels[step]}`}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>
    </Paper>
  );
}
export function DraftConfirmation({
  confirmation,
  setConfirmation,
  discard,
  resume,
}: {
  confirmation: Confirmation;
  setConfirmation: (value: Confirmation) => void;
  discard: () => void;
  resume: () => void;
}) {
  return (
    <Dialog
      open={confirmation !== null}
      onClose={() => setConfirmation(null)}
      aria-labelledby="selling-confirm-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="selling-confirm-title">
        {confirmation === "discard"
          ? "Discard this local draft?"
          : "Resume your saved draft?"}
      </DialogTitle>
      <DialogContent>
        {confirmation === "discard"
          ? "This removes the saved draft for this account on this device and clears your current entries and photos. This cannot be undone."
          : "Your current unsaved entries and selected photos will be replaced with the saved text draft."}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmation(null)}>Keep Editing</Button>
        <Button
          color={confirmation === "discard" ? "error" : "primary"}
          onClick={confirmation === "discard" ? discard : resume}
        >
          {confirmation === "discard" ? "Discard Draft" : "Resume Draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
